import { useState } from 'react';
import {
  NOTIFICATION_ENTITY_LABELS,
  NOTIFICATION_TRIGGER_LABELS,
  type Notification,
} from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { NotificationFormModal } from './NotificationFormModal.js';
import { useNotifications } from './api.js';

function statusBadge(n: Notification): JSX.Element {
  if (!n.enabled) return <Badge tone="gray">Disabled</Badge>;
  if (n.lastRunStatus === 'failed') return <Badge tone="red">Last run failed</Badge>;
  return <Badge tone="green">Enabled</Badge>;
}

export function NotificationsPage(): JSX.Element {
  const { isAdmin } = useAuth();
  const notificationsQuery = useNotifications();
  const [editing, setEditing] = useState<Notification | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="Notifications" />
        <Card>
          <p className="text-sm text-slate-600">
            You need administrator privileges to manage notifications.
          </p>
        </Card>
      </div>
    );
  }

  const openCreate = (): void => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (n: Notification): void => {
    setEditing(n);
    setModalOpen(true);
  };

  const notifications = notificationsQuery.data ?? [];

  return (
    <div>
      <PageHeader
        title="Notifications"
        description="Email people automatically when records change — e.g. a new unassigned ticket."
        actions={<Button onClick={openCreate}>New notification</Button>}
      />

      <Card>
        <DataState
          isLoading={notificationsQuery.isLoading}
          isError={notificationsQuery.isError}
          error={notificationsQuery.error}
          onRetry={() => void notificationsQuery.refetch()}
          isEmpty={notifications.length === 0}
          emptyDescription="No notifications yet. Create one to start watching for changes."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Watches</th>
                  <th className="px-3 py-2 font-medium">Trigger</th>
                  <th className="px-3 py-2 font-medium">Recipients</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Last run</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {notifications.map((n) => (
                  <tr
                    key={n.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => openEdit(n)}
                  >
                    <td className="px-3 py-2 font-medium text-slate-900">{n.name}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {NOTIFICATION_ENTITY_LABELS[n.entity]}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {NOTIFICATION_TRIGGER_LABELS[n.trigger]}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {n.recipients.length + n.recipientSources.length}
                    </td>
                    <td className="px-3 py-2">{statusBadge(n)}</td>
                    <td className="px-3 py-2 text-slate-500">
                      {n.lastRunAt ? new Date(n.lastRunAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DataState>
      </Card>

      <NotificationFormModal
        isOpen={modalOpen}
        notification={editing}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
